import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, Loader2 } from "lucide-react";
import { GenerationCard } from "@/components/create/GenerationCard";
import { ImageForm } from "@/components/create/ImageForm";
import { ComingSoonHero, QaNotice } from "@/components/create/StudioNotices";
import { MODES, StudioTabs } from "@/components/create/StudioTabs";
import { PresetGrid } from "@/components/create/explore/PresetGrid";
import { StudioHeader } from "@/components/create/explore/StudioHeader";
import { hfOpen, imageOpen, useGenerations, useMediaConfig, useOpenPreset, usePresets } from "@/lib/studio";
import { ProStudio } from "@/components/create/ProStudio";
import { Layers } from "lucide-react";

const IMAGE_MODES = [...MODES.filter((m) => m.key !== "video"), { key: "pro", label: "Pro models", icon: Layers }];

function Locked({ covers }) {
  return (
    <ComingSoonHero covers={covers} title="Image studio opens soon"
      body="Create images from a description and edit your own photos with one sentence, in Budget or Premium quality. You can already browse every preset.">
      <Link to="/create/presets" className="mt-2 inline-flex h-10 w-fit items-center gap-1 rounded-full bg-white px-5 text-sm font-semibold text-ink-950 transition-opacity hover:opacity-90" data-testid="coming-soon-browse-presets">
        Browse presets <ChevronRight className="h-4 w-4" />
      </Link>
    </ComingSoonHero>
  );
}

export default function ImageStudioPage() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const mode = ["edit", "pro"].includes(params.get("mode")) ? params.get("mode") : "create";
  const [sources, setSources] = useState(() => location.state?.sources || []);
  const config = useMediaConfig().data;
  const onOpen = useOpenPreset();
  const imagePresets = (usePresets().data?.items || []).filter((p) => p.kind === "image" && p.status === "published");
  const covers = imagePresets.map((p) => p.cover_url);
  const items = (useGenerations().data?.items || []).filter((g) => g.kind === "image");
  const open = imageOpen(config) || hfOpen(config);
  const setMode = (m) => setParams(m === "create" ? {} : { mode: m }, { replace: true });
  const editThis = (g) => {
    setSources((list) => (list.some((s) => s.id === g.id) ? list : [...list, { id: g.id, preview_url: g.thumb_url }].slice(-config.image.max_sources)));
    setMode("edit");
    document.querySelector("[data-testid='image-studio-page']")?.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <div className="h-full overflow-y-auto" data-testid="image-studio-page">
      <StudioHeader title="Image" back />
      <div className="mx-auto w-full max-w-[1040px] space-y-5 px-4 pb-20 pt-5 sm:px-6 sm:pt-8">
        {!config && <div className="flex h-40 items-center justify-center text-zinc-500" data-testid="studio-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>}
        {config && !open && <Locked covers={covers} />}
        {config && !open && imagePresets.length > 0 && (
          <section className="space-y-4 pt-4" data-testid="image-presets">
            <h2 className="text-xl font-bold tracking-tight text-white sm:text-2xl">Image presets</h2>
            <PresetGrid presets={imagePresets} onOpen={onOpen} />
          </section>
        )}
        {config && open && (
          <>
            <StudioTabs modes={IMAGE_MODES.filter((m) => (m.key === "pro" ? hfOpen(config) : imageOpen(config)))} mode={mode} onChange={setMode} />
            {(config.image.access === "qa" || config.higgsfield?.access === "qa") && <QaNotice budget={config.image.qa_budget} />}
            {mode === "pro" ? <ProStudio testId="image-pro-studio" filter={(m) => m.kind === "image" && m.studio === "image"} initialModelId="higgsfield-ai/soul/v2/standard" />
              : imageOpen(config) && <ImageForm key={mode} mode={mode} offer={config.image} sources={sources} setSources={setSources} />}
          </>
        )}
        {items.length > 0 && (
          <section className="pt-6" data-testid="image-history">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold tracking-tight text-white md:text-lg">Your images</h2>
              <button type="button" onClick={() => navigate("/create/assets")} className="flex items-center gap-0.5 text-xs font-medium text-zinc-400 hover:text-white" data-testid="image-history-assets-link">
                All assets <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3" data-testid="creations-grid">
              {items.map((g) => <GenerationCard key={g.id} g={g} onEdit={open ? editThis : null} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
